<template>
    <ResponsiveDialog
        v-model="internalValue"
        :max-width="920"
        :header-title="$t('dashboard.typeMatrix.title')"
    >
        <v-card-text class="type-matrix__scroll">
            <p class="type-matrix__hint">
                {{ $t('dashboard.typeMatrix.hint') }}
            </p>
            <div class="type-matrix__table-wrap">
                <table v-if="loaded" class="type-matrix__table">
                    <thead>
                        <tr>
                            <th class="type-matrix__corner" :style="cornerStyle"></th>
                            <th
                                v-for="typeId in headers"
                                :key="'col-' + typeId"
                                class="type-matrix__head type-matrix__head--col"
                                :style="headerStyle(typeId)"
                            >{{ shortName(typeId) }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(row, i) in table" :key="'row-' + headers[i]">
                            <th
                                class="type-matrix__head type-matrix__head--row"
                                :style="headerStyle(headers[i])"
                            >{{ shortName(headers[i]) }}</th>
                            <td
                                v-for="(value, j) in row"
                                :key="'cell-' + headers[i] + '-' + headers[j]"
                                class="type-matrix__cell"
                                :class="cellClass(value)"
                            >{{ cellLabel(value) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="type-matrix__scroll-spacer"/>
        </v-card-text>
    </ResponsiveDialog>
</template>

<script>
    import {mapActions} from 'vuex';
    import ResponsiveDialog from '@/components/utils/ResponsiveDialog';
    import {TYPE_COLORS, TYPE_DARK_THEME} from '@/utils/typeColorEnum';
    import {flags} from '@/config/featureFlags';

    export default {
        name: 'TypeMatrixDialog',
        components: {ResponsiveDialog},
        props: {
            value: {type: Boolean, default: false},
        },
        data() {
            return {
                headers: [],
                table: [],
                loaded: false,
            }
        },
        computed: {
            // rohova bunka nema typovou barvu (na rozdil od ostatnich sticky hlavicek) - potrebuje vlastni theme-adaptivni neprusvitne pozadi
            cornerStyle() {
                return {backgroundColor: flags.darkTheme ? '#1e1e1e' : '#ffffff'};
            },
            internalValue: {
                get() {
                    return this.value;
                },
                set(v) {
                    this.$emit('input', v);
                },
            },
        },
        watch: {
            value(isOpen) {
                if (isOpen && !this.loaded) {
                    this.loadTable();
                }
            },
        },
        methods: {
            ...mapActions({
                loadTypeTable: 'dashboard/loadTypeTable',
            }),
            loadTable() {
                this.loadTypeTable().then((data) => {
                    this.headers = data.headers;
                    this.table = data.table;
                    this.loaded = true;
                });
            },
            shortName(typeId) {
                return typeId.slice(0, 3).charAt(0) + typeId.slice(1, 3).toLowerCase();
            },
            headerStyle(typeId) {
                return {
                    backgroundColor: TYPE_COLORS[typeId],
                    color: TYPE_DARK_THEME[typeId] ? '#fff' : '#000',
                };
            },
            cellClass(value) {
                if (value === 1.5) {
                    return 'type-matrix__cell--strong';
                }
                if (value === 0.5) {
                    return 'type-matrix__cell--weak';
                }
                return 'type-matrix__cell--neutral';
            },
            cellLabel(value) {
                // neutral (1x) se zamerne nevypisuje jako "1" - v 18x18 mrizce by to byl vizualni sum
                return value === 1 ? '' : value;
            },
        },
    }
</script>

<style scoped>
    .type-matrix__scroll {
        flex: 1 1 auto;
        min-height: 0;
        overflow: auto;
    }

    .type-matrix__hint {
        font-size: 0.85rem;
        opacity: 0.8;
        margin-bottom: 8px;
    }

    .type-matrix__table-wrap {
        overflow: auto;
    }

    .type-matrix__table {
        border-collapse: collapse;
        font-size: 0.75rem;
    }

    .type-matrix__corner {
        position: sticky;
        top: 0;
        left: 0;
        z-index: 3;
    }

    .type-matrix__head {
        padding: 4px 6px;
        font-weight: 700;
        text-align: center;
        white-space: nowrap;
        border: 1px solid rgba(0, 0, 0, 0.3);
    }

    .type-matrix__head--col {
        position: sticky;
        top: 0;
        z-index: 2;
    }

    .type-matrix__head--row {
        position: sticky;
        left: 0;
        z-index: 1;
    }

    .type-matrix__cell {
        width: 30px;
        height: 26px;
        text-align: center;
        border: 1px solid rgba(255, 255, 255, 0.15);
    }

    .type-matrix__cell--strong {
        background-color: rgba(76, 175, 80, 0.55);
    }

    .type-matrix__cell--weak {
        background-color: rgba(244, 67, 54, 0.5);
    }

    .type-matrix__cell--neutral {
        background-color: rgba(255, 255, 255, 0.04);
    }

    /* stejny princip jako u TypeChartDialog - misto pro plovouci zaviraci krizek na mobilu */
    .type-matrix__scroll-spacer {
        height: var(--responsive-dialog-close-clearance);
    }
</style>
